import React from 'react';
import PropTypes from 'prop-types';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { cn } from '@/lib/utils';

// Helper function to get background color class for the grid squares (Moved here)
const getGridBgClass = (avgScore) => {
  if (avgScore == null || avgScore === 0) return 'bg-gray-200';
  if (avgScore < 1.5) return 'bg-score-1';
  if (avgScore < 2.5) return 'bg-score-2';
  if (avgScore < 3.5) return 'bg-score-3';
  if (avgScore < 4.5) return 'bg-score-4';
  return 'bg-score-5';
};

const AssessmentSummaryReportDisplay = ({
  structure,
  factorStats,
  contributorSummaries,
  assessmentType,
  getFactorBadgeStyle,
  getFactorLabel,
  getSectionSubtitleElement,
  currentViewIdentifier,
  displayMode = 'both' // 'both', 'grid', or 'accordion'
}) => {

  // Create a map for quick lookup of factor stats by factor ID
  const factorStatsMap = React.useMemo(() => {
    return (factorStats || []).reduce((acc, factor) => {
      acc[factor.key] = factor; // Use factor.key which should be the factor ID
      return acc;
    }, {});
  }, [factorStats]);

  // Create a map for quick lookup of contributor summaries by contributor ID
  const contributorSummariesMap = React.useMemo(() => {
    return (contributorSummaries || []).reduce((acc, summary) => {
      acc[summary.key] = summary; // Use summary.key which should be the contributor ID
      return acc;
    }, {});
  }, [contributorSummaries]);

  // Helper to render the section subtitle with new badge logic for 'full_detailed'
  const renderSectionSubtitle = (summary) => {
    if (assessmentType !== 'full_detailed') {
      // Use the provided function for non-detailed
      return getSectionSubtitleElement(summary, assessmentType);
    }
    // For full_detailed, show score as plain text and badge only the level
    // If section is not started, show only the badge
    const totalFactors = summary.total_factors;
    const answeredFactors = summary.answered_factors;
    if (answeredFactors === 0) {
      // Not Started badge only
      const baseBadgeClasses = "px-2 py-0.5 rounded-full text-xs font-semibold font-sans mr-1";
      return (
        <span className={cn(baseBadgeClasses, 'bg-gray-200', 'text-gray-700')}>Not Started</span>
      );
    }
    // Otherwise, show score and badge
    const score = summary.total_score || summary.participant_total_score;
    let level = 'Unknown';
    let scoreBgColor = 'bg-gray-200';
    let scoreTextColor = 'text-gray-700';
    if (score >= 46)      { level = 'Extending'; scoreBgColor = 'bg-score-5'; scoreTextColor = 'text-score-5-foreground'; }
    else if (score >= 40) { level = 'Proficient'; scoreBgColor = 'bg-score-4'; scoreTextColor = 'text-score-4-foreground'; }
    else if (score >= 32) { level = 'Developing'; scoreBgColor = 'bg-score-3'; scoreTextColor = 'text-score-3-foreground'; }
    else if (score >= 22) { level = 'Emerging'; scoreBgColor = 'bg-score-2'; scoreTextColor = 'text-score-2-foreground'; }
    else if (score >= 10) { level = 'Initiating'; scoreBgColor = 'bg-score-1'; scoreTextColor = 'text-score-1-foreground'; }
    const baseBadgeClasses = "px-2 py-0.5 rounded-full text-xs font-semibold font-sans mr-1";
    return (
      <>
        <span className="mr-2">Score: {score}</span>
        <span className={cn(baseBadgeClasses, scoreBgColor, scoreTextColor)}>{level}</span>
      </>
    );
  };

  // Helper to get grid color for simple assessment
  const getSimpleGridColor = (factor) => {
    const avgScore = factor.avg_score;
    // Not answered
    if (avgScore == null) return 'bg-gray-200';
    // Mixed (based on average score range) - This check should come before S/O specific checks
    if (avgScore > 2.0 && avgScore < 4.0) return 'bg-gray-400';
    // Strength only (avgScore is 4.0, so factor.hasStrength should be true based on previous logic)
    if (factor.hasStrength) return 'bg-score-5';
    // Opportunity only (avgScore is 2.0, so factor.hasOpportunity should be true based on previous logic)
    if (factor.hasOpportunity) return 'bg-score-3';
    
    return 'bg-gray-200'; // Default fallback if somehow missed
  };

  // Helper to get badge color for S/O/M in simple assessment
  const getSimpleBadgeColor = (type) => {
    if (type === 'S') return 'bg-score-5 text-score-5-foreground';
    if (type === 'O') return 'bg-score-3 text-score-3-foreground';
    if (type === 'M') return 'bg-gray-400 text-gray-100';
    return 'bg-gray-200 text-gray-700';
  };

  if (!structure || structure.length === 0 || !factorStats || !contributorSummaries) {
    // Optional: Render a loading state or placeholder if data isn't ready
    return <div className="p-4 text-center text-gray-500">Loading report data...</div>;
  }

  const shouldShowGrid = displayMode === 'both' || displayMode === 'grid';
  const shouldShowAccordion = displayMode === 'both' || displayMode === 'accordion';

  return (
    <>
      {/* Visual grid for factors by average score */}
      {shouldShowGrid && (
      <div className={displayMode === 'both' ? 'mt-8' : ''}>
        {displayMode === 'both' && <h3 className="text-xl mb-2">Change Success Assessment Factors (X axis)</h3>}
        <div className="flex">
          <div className="w-1/5 text-right font-medium text-sm text-gray-600 pr-4">
            <span className="hidden sm:inline">Contributor (Y axis)</span>
            <span className="sm:hidden">Cont. (Y axis)</span>
          </div>
          <div className="w-4/5 grid grid-cols-10 gap-1 text-center text-xs text-gray-500">
            {Array.from({ length: 10 }, (_, i) => (
              <div key={i}>{i + 1}</div>
            ))}
          </div>
        </div>
        {structure.map((contributor, index) => {
          // For each factor in the structure, get its stats from the map
          const factorsWithStats = (contributor.factors || []).map((factor) => {
            const stats = factorStatsMap[factor.id] || {}
            const avgScore = stats.avg_score
            let displayHasStrength = stats.hasStrength // Default to what's provided
            let displayHasOpportunity = stats.hasOpportunity

            // For the quick_interactive grid, derive S/O from avg_score for aggregate view
            if (assessmentType === 'quick_interactive') {
              if (avgScore != null) {
                displayHasStrength = avgScore == 4.0
                displayHasOpportunity = avgScore == 2.0
              } else {
                displayHasStrength = false
                displayHasOpportunity = false
              }
            }

            return {
              ...factor,
              avg_score: avgScore,
              response_count: stats.response_count || stats.total_responses || 0,
              // Use the derived values for display in the grid for quick_interactive
              hasStrength: displayHasStrength,
              hasOpportunity: displayHasOpportunity,
              // Keep original participant-specific flags if needed by label/badge functions passed as props
              participant_strength: stats.participant_strength,
              participant_opportunity: stats.participant_opportunity,
              participant_answered: stats.participant_answered,
              participant_score: stats.participant_score,
            }
          })

          return (
            <div key={contributor.id} className="flex items-center">
              <div className="w-1/5 text-right text-sm font-medium pr-4 whitespace-nowrap">
                <span className="hidden sm:inline">Contributor</span>
                <span className="sm:hidden">Cont.</span> {index + 1}
              </div>
              <div className="w-4/5 mb-1 grid grid-cols-10 gap-1">
                {factorsWithStats.map(
                  (factor) => (
                    console.log('factor', factor),
                    (
                      <div
                        key={factor.id}
                        className={`w-full aspect-square rounded flex items-center justify-center text-xs font-semibold ${assessmentType === 'quick_interactive' ? getSimpleGridColor(factor) : getGridBgClass(factor.avg_score)}`}
                        title={`${factor.text}
Avg Score: ${factor.avg_score ? factor.avg_score.toFixed(1) : '—'}${factor.response_count ? ` (Responses: ${factor.response_count})` : ''}`}
                      ></div>
                    )
                  )
                )}
              </div>
            </div>
          )
        })}
        <div className="flex justify-between items-center pt-4 text-sm text-gray-600">
          <div className="flex items-center space-x-1 gap-4 justify-end w-full">
            <strong className="whitespace-nowrap">Color Key:</strong>
            {assessmentType === 'quick_interactive' ? (
              <ul className="flex flex-row items-center space-x-1 gap-2 flex-wrap">
                <li className="flex flex-row items-center space-x-1 gap-1">
                  <div className="w-3 h-3 rounded bg-score-3"></div>
                  <span>Opportunity</span>
                </li>
                <li className="flex flex-row items-center space-x-1 gap-1">
                  <div className="w-3 h-3 rounded bg-score-5"></div>
                  <span>Strength</span>
                </li>
                {(currentViewIdentifier === 'all' || !currentViewIdentifier) && (
                  <li className="flex flex-row items-center space-x-1 gap-1">
                    <div className="w-3 h-3 rounded bg-gray-400"></div>
                    <span>Mixed</span>
                  </li>
                )}
                <span className="text-xs text-gray-400">(light gray = not answered)</span>
              </ul>
            ) : (
              <>
                <span>Low</span>
                <div className="w-3 h-3 rounded bg-score-1"></div>
                <div className="w-3 h-3 rounded bg-score-2"></div>
                <div className="w-3 h-3 rounded bg-score-3"></div>
                <div className="w-3 h-3 rounded bg-score-4"></div>
                <div className="w-3 h-3 rounded bg-score-5"></div>
                <span className="ml-1">High</span>
                <span className="ml-2 text-xs text-gray-400">(grey = not answered)</span>
              </>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Accordion for detailed view */}
      {shouldShowAccordion && (
      <Accordion type="multiple" defaultValue={[]} className={`w-full ${displayMode === 'both' ? 'mt-8' : ''}`}>
        {structure.map((contributor, index) => {
          const summary = contributorSummariesMap[contributor.id]
          const factorsForContributor = (contributor.factors || []).map((f) => factorStatsMap[f.id]).filter(Boolean) // Get stats for factors in this section

          // Ensure summary exists before trying to render the item
          if (!summary) {
            console.warn(`Summary not found for contributor ID: ${contributor.id}`)
            return null // Skip rendering if summary data is missing
          }

          // For simple assessment, override the badge colors for S/O/M
          let subtitleElement
          if (assessmentType === 'quick_interactive') {
            const baseBadgeClasses = 'px-2 py-0.5 rounded-full text-xs font-semibold font-sans mr-1'
            // Use direct aggregate counts. These should now be correctly calculated based on factor averages.
            const S = summary.strength_count
            const O = summary.opportunity_count
            const M = summary.mixed_count
            // Check if this is a single participant view (not 'all')
            const isSingleView = currentViewIdentifier && currentViewIdentifier !== 'all'
            subtitleElement = (
              <>
                <span className={cn(baseBadgeClasses, getSimpleBadgeColor('S'))}>S: {S ?? 0}</span>
                <span className={cn(baseBadgeClasses, getSimpleBadgeColor('O'))}>O: {O ?? 0}</span>
                <span className={cn(baseBadgeClasses, getSimpleBadgeColor('M'))}>M: {M ?? 0}</span>
                {!isSingleView && ( // Only show Mixed count for 'all' view
                  <span className={cn(baseBadgeClasses, getSimpleBadgeColor('M'))}>M: {M ?? 0}</span>
                )}
              </>
            )
          } else {
            subtitleElement = renderSectionSubtitle(summary)
          }

          return (
            <AccordionItem value={`item-${index}`} key={summary.key || contributor.id}>
              <AccordionTrigger>
                <div className="flex justify-between items-center w-full pr-4">
                  {/* Title Part: Make it grow, align text to left, and responsive */}
                  <span className="flex-grow text-left hover:underline cursor-pointer">
                    <span className="sm:hidden hover:underline">Contributor {index + 1}</span>
                    <span className="hidden sm:inline hover:underline font-sans text-sm font-semibold text-black">
                      Contributor {index + 1}: {summary.name}
                    </span>
                  </span>
                  {/* Subtitle/Badge Part: Ensure it doesn't shrink and stays to the right */}
                  <span className="text-sm ml-auto flex-shrink-0">{subtitleElement}</span>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                {factorsForContributor.length > 0 ? (
                  <ul className="list-none pl-4 space-y-2">
                    {factorsForContributor.map((factor) => {
                      // Ensure factor object is valid before rendering
                      if (!factor || !factor.key) {
                        console.warn('Invalid factor data encountered:', factor)
                        return null
                      }
                      const responseLabel = getFactorLabel(factor, assessmentType)
                      const badgeStyle = getFactorBadgeStyle(factor, assessmentType)

                      return (
                        <li key={factor.key} className="flex items-center text-sm">
                          {/* Use factor.text which should come from factorStats */}
                          <span className="flex-grow mr-2">{factor.text}</span>
                          <span className={cn('ml-auto whitespace-nowrap', badgeStyle)}>{responseLabel}</span>
                        </li>
                      )
                    })}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500 pl-4">No responses recorded for this section yet.</p>
                )}
              </AccordionContent>
            </AccordionItem>
          )
        })}
      </Accordion>
      )}
    </>
  )
};

AssessmentSummaryReportDisplay.propTypes = {
  structure: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    factors: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      text: PropTypes.string
    }))
  })).isRequired,
  factorStats: PropTypes.arrayOf(PropTypes.object).isRequired, // More specific shape can be added
  contributorSummaries: PropTypes.arrayOf(PropTypes.object).isRequired, // More specific shape can be added
  assessmentType: PropTypes.string.isRequired,
  getFactorBadgeStyle: PropTypes.func.isRequired,
  getFactorLabel: PropTypes.func.isRequired,
  getSectionSubtitleElement: PropTypes.func.isRequired,
  currentViewIdentifier: PropTypes.string,
  displayMode: PropTypes.oneOf(['both', 'grid', 'accordion'])
};

export default AssessmentSummaryReportDisplay; 